import { useCallback, useEffect, useRef, useState } from "react";
import {
  Account, ActivityEvent, api, API_BASE, BoardSession, Directive, getActiveBook, Manifest, RiskState,
} from "../api";
import { fmtTime } from "../format";
import { signedUsd, usdCompact } from "../lib/money";
import { MoneyBadge } from "./MoneyScope";

// The BOOK'S cockpit, slim by design: state, the sleeves at a glance, the
// regime in one line, directives, and the live feed. Deep dives live on their
// own pages — Signals (intelligence), Plan (strikes), Holdings (positions),
// Funding & Dials (allocation).
type MacroBrief = { brief: { regime?: string } };
type Position = { asset_class: string; unrealized_pnl: number | null };
type Holdings = { budget: number; deployed: number; holdings: Position[] };
type SleeveOverview = { budget: number; deployed: number; positions: unknown[] };

const KIND_TONE: Record<string, string> = {
  error: "text-down", incident: "text-down", kill: "text-down",
  flatten: "text-gold", risk: "text-gold", trigger: "text-gold",
  order: "text-up", fill: "text-up",
  directive: "text-accent", ruling: "text-gold", session: "text-accent", perspective: "text-accent",
};

export default function Command({ manifest }: { manifest: Manifest }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [acctErr, setAcctErr] = useState<string | null>(null);
  const [risk, setRisk] = useState<RiskState | null>(null);
  const [directives, setDirectives] = useState<Directive[]>([]);
  const [session, setSession] = useState<BoardSession | null>(null);
  const [regime, setRegime] = useState<string>("");
  const [hold, setHold] = useState<Holdings | null>(null);
  const [crypto, setCrypto] = useState<SleeveOverview | null>(null);
  const [fx, setFx] = useState<SleeveOverview | null>(null);
  const [cmdty, setCmdty] = useState<(SleeveOverview & { enabled: boolean }) | null>(null);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const load = useCallback(async () => {
    try { setAccount(await api.get<Account>("/api/trading/account")); setAcctErr(null); }
    catch (e) { setAcctErr((e as Error).message); }
    try {
      setRisk(await api.get<RiskState>("/api/risk/state"));
      setDirectives(await api.get<Directive[]>("/api/board/directives"));
      const s = await api.get<BoardSession[]>("/api/board/sessions?limit=1");
      setSession(s[0] ?? null);
      setHold(await api.get<Holdings>("/api/longterm/holdings"));
      api.get<SleeveOverview>("/api/crypto/overview").then(setCrypto).catch(() => {});
      api.get<SleeveOverview>("/api/forex/overview").then(setFx).catch(() => {});
      api.get<SleeveOverview & { enabled: boolean }>("/api/commodities/sleeve")
        .then(setCmdty).catch(() => {});
      api.get<MacroBrief>("/api/board/macro").then((m) => setRegime(m.brief?.regime ?? "")).catch(() => {});
    } catch { /* transient */ }
  }, []);

  useEffect(() => {
    void load();
    const iv = setInterval(load, 15000);
    const es = new EventSource(`${API_BASE}/api/stream/events`, { withCredentials: true });
    es.onmessage = (m) => { try { setEvents((p) => [...p.slice(-119), JSON.parse(m.data)]); } catch { /**/ } };
    esRef.current = es;
    (async () => { try { setEvents(await api.get<ActivityEvent[]>("/api/trading/events?limit=40")); } catch { /**/ } })();
    return () => { clearInterval(iv); es.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function convene() {
    setBusy("convene");
    try { await api.post("/api/board/convene?kind=morning"); await load(); }
    finally { setBusy(null); }
  }

  // Day P&L against the SAME base as the Equity tile beside it. The all-venue
  // snapshot (start_total_equity) is the honest base; when it hasn't been
  // marked yet we fall back to the old Alpaca-only calc and SAY so in the
  // label — two adjacent tiles answering different questions unlabeled was
  // Kaizen D1/F1.
  const allVenuePnl = account && risk?.start_total_equity != null
    ? account.total_equity - risk.start_total_equity : null;
  const dayPnl = allVenuePnl ?? (account && risk?.start_equity ? account.equity - risk.start_equity : null);
  const dayPnlLabel = allVenuePnl != null ? "Day P&L (all venues)" : "Day P&L (Alpaca)";
  const running = session?.status === "running";
  const st = risk
    ? (risk.tenant_halted ? "HALTED" : risk.halted ? "DAY HALT" : risk.no_new_entries ? "WIND-DOWN" : "TRADING")
    : "—";

  const sleeve = (name: string, rows: Position[] | undefined, budget?: number, deployed?: number) => {
    const pnl = (rows ?? []).reduce((a, p) => a + (p.unrealized_pnl ?? 0), 0);
    return { name, count: rows?.length ?? 0, pnl, budget, deployed };
  };
  const eqRows = hold?.holdings.filter((p) => p.asset_class === "equity");
  const cRows = hold?.holdings.filter((p) => p.asset_class === "crypto");
  const fRows = hold?.holdings.filter((p) => p.asset_class === "forex");
  const cmRows = hold?.holdings.filter((p) => p.asset_class === "commodity");
  const sleeves = [
    sleeve("Equity", eqRows, hold?.budget, hold?.deployed),
    sleeve("Crypto", cRows, crypto?.budget, crypto?.deployed),
    sleeve("Forex", fRows, fx?.budget, fx?.deployed),
    // Commodities shares the Alpaca account with equities rather than having a
    // venue balance of its own, so its card only appears where the sleeve is
    // actually armed — an always-visible $0 card in the live book would read
    // as a broken sleeve rather than a deliberately disarmed one.
    ...(cmdty?.enabled ? [sleeve("Commodities", cmRows, cmdty.budget, cmdty.deployed)] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h1 className="text-lg font-bold">Command</h1>
          {/* Every figure on this page is ONE book's money — say which kind
              here, not only in the frame chip. `account.env` is Alpaca's
              environment, which reads like a money type and isn't one. */}
          <MoneyBadge money={getActiveBook().startsWith("live") ? "real" : "paper"} />
        </div>
        <span className="text-xs text-slate-500">{manifest.tenant}&rsquo;s firm · alpaca env {account?.env?.toUpperCase() ?? "—"}</span>
      </div>

      {/* State strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Equity" value={account ? usdCompact(account.total_equity || account.equity) : "—"}
              sub={acctErr ? "Alpaca not connected" : venueBreakdown(account)}
              warn={venueWarning(account)} />
        <Stat label={dayPnlLabel} value={dayPnl === null ? "—" : signedUsd(dayPnl)}
              tone={dayPnl === null ? undefined : dayPnl >= 0 ? "up" : "down"} />
        <Stat label="State" value={st}
              tone={risk && (risk.tenant_halted || risk.halted) ? "down" : "up"} />
        <Stat label="Buying power" value={account ? usdCompact(account.buying_power) : "—"} />
      </div>

      {/* Regime one-liner — the full read lives on Signals */}
      {regime && (
        <div className="card py-2 text-sm text-slate-300 flex items-baseline gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0">Regime</span>
          <span>{regime}</span>
          <span className="text-xs text-slate-600 ml-auto shrink-0">full read → Signals</span>
        </div>
      )}

      {/* The sleeves at a glance */}
      <div className={`grid gap-3 ${sleeves.length > 3 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-3"}`}>
        {sleeves.map((s) => (
          <div key={s.name} className="card py-2">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-bold text-slate-300">{s.name}</span>
              <span className={`text-xs font-mono ${s.pnl >= 0 ? "text-up" : "text-down"}`}>
                {s.count > 0 ? signedUsd(s.pnl) : "—"}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 font-mono mt-0.5">
              {s.count} open{s.budget != null ? ` · ${usdCompact(s.deployed ?? 0)} / ${usdCompact(s.budget)}` : ""}
            </div>
          </div>
        ))}
      </div>

      {/* STONKY + directives */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="label mb-0">Stonky</h2>
            <button className="btn text-xs" onClick={convene} disabled={busy === "convene" || running}>
              {busy === "convene" || running ? "In session…" : "Convene the board"}
            </button>
          </div>
          {running && (
            <div className="text-sm text-slate-400 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" /> The board is deliberating…
            </div>
          )}
          {session?.summary && <p className="text-sm text-slate-300">{session.summary}</p>}
          {stonkyRuling(session) && (
            <p className="text-sm text-gold italic border-l-2 border-gold pl-2">
              &ldquo;{stonkyRuling(session)}&rdquo;
            </p>
          )}
          {!session && <div className="text-sm text-slate-500">No session yet in this book. Convene the board to set directives.</div>}
        </div>
        <div className="card">
          <h2 className="label">Active directives</h2>
          {directives.length === 0 && <div className="text-slate-500 text-sm">None — the desk stands down.</div>}
          <ol className="space-y-2">
            {directives.map((d) => (
              <li key={d.id} className="text-sm flex gap-2">
                <span className="text-accent font-mono shrink-0">{d.seq}.</span><span>{d.text}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Watch it unfold */}
      <div className="card">
        <h2 className="label">Watch it unfold</h2>
        <div className="h-64 overflow-y-auto font-mono text-xs space-y-1">
          {[...events].reverse().map((e) => (
            <div key={e.id} className="flex gap-2">
              <span className="text-slate-600 shrink-0">{fmtTime(e.ts)}</span>
              <span className={`shrink-0 ${KIND_TONE[e.kind] ?? "text-slate-400"}`}>{e.kind}</span>
              <span className="text-slate-500 shrink-0">{e.actor}</span>
              <span className="truncate text-slate-300">{summarize(e)}</span>
            </div>
          ))}
          {events.length === 0 && <div className="text-slate-600">quiet — nothing happening yet</div>}
        </div>
      </div>
    </div>
  );
}

function stonkyRuling(s: BoardSession | null): string | null {
  const d = s?.deliberation as { stonky?: { statement?: string } } | null;
  return d?.stonky?.statement ?? null;
}
function Stat({ label, value, sub, tone, warn }: {
  label: string; value: string; sub?: string; tone?: "up" | "down"; warn?: string;
}) {
  return (
    <div className="card">
      <div className="label">{label}</div>
      <div className={`text-xl font-bold font-mono ${tone === "up" ? "text-up" : tone === "down" ? "text-down" : ""}`}>{value}</div>
      {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
      {warn ? <div className="badge bg-gold/15 text-gold mt-1">{warn}</div> : null}
    </div>
  );
}
const VENUE_LABEL: Record<string, string> = { alpaca: "Alpaca", crypto: "Crypto", forex: "Forex" };
function venueBreakdown(account: Account | null): string | undefined {
  const venues = account?.venues;
  if (!venues) return account?.status;
  const keys = Object.keys(venues);
  if (keys.length <= 1) return account?.status;
  return keys.map((k) => `${VENUE_LABEL[k] ?? k} ${usdCompact(venues[k])}`).join(" · ");
}
// D2/F6: a venue read failure used to silently shrink total_equity by that
// venue's whole balance — indistinguishable from a loss. Now the backend
// records the failure and the tile wears it.
function venueWarning(account: Account | null): string | undefined {
  const errs = account?.venue_errors ?? [];
  if (errs.length === 0) return undefined;
  const names = errs.map((e) => e.split(":")[0].trim()).join(", ");
  return `total excludes ${names} — venue unreachable`;
}
function summarize(e: ActivityEvent): string {
  const p = e.payload ?? {};
  for (const k of ["text", "title", "event", "statement"]) if (typeof p[k] === "string") return p[k] as string;
  if (typeof p.symbol === "string") return `${p.symbol} ${p.side ?? ""} ${p.qty ?? ""}`;
  return JSON.stringify(p).slice(0, 120);
}
